import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { IAuthState, IUser, UserRole } from "../types/auth.types";

const getInitialAdminState = (): IAuthState => {
    const token = localStorage.getItem("admin_token");
    const spotfixToken = localStorage.getItem("admin_spotfix_token") || localStorage.getItem("spotfix_token");
    const role = localStorage.getItem("admin_role") as UserRole | null;
    const userStr = localStorage.getItem("admin_user");
    let user: IUser | null = null;

    if (userStr) {
        try {
            user = JSON.parse(userStr);
        } catch (e) {
            console.error("Failed to parse admin user from localStorage", e);
        }
    }

    return {
        token,
        spotfixToken,
        role,
        user,
        loading: false,
        error: null,
    };
};

const initialState: IAuthState = getInitialAdminState();

const adminAuthSlice = createSlice({
    name: "adminAuth",
    initialState,
    reducers: {
        adminLoginSuccess: (
            state,
            action: PayloadAction<{
                token: string;
                spotfixToken?: string | null;
                role: UserRole;
                user: IUser;
            }>
        ) => {
            state.token = action.payload.token;
            state.spotfixToken = action.payload.spotfixToken || action.payload.user?.spotfixToken || null;
            state.role = action.payload.role;
            state.user = action.payload.user;
            state.error = null;

            localStorage.setItem("admin_token", action.payload.token);
            if (state.spotfixToken) {
                localStorage.setItem("admin_spotfix_token", state.spotfixToken);
                localStorage.setItem("spotfix_token", state.spotfixToken);
            }
            localStorage.setItem("admin_role", action.payload.role);
            localStorage.setItem("admin_user", JSON.stringify(action.payload.user));
        },

        adminLogout: (state) => {
            state.token = null;
            state.spotfixToken = null;
            state.role = null;
            state.user = null;
            state.error = null;

            localStorage.removeItem("admin_token");
            localStorage.removeItem("admin_spotfix_token");
            localStorage.removeItem("spotfix_token");
            localStorage.removeItem("admin_role");
            localStorage.removeItem("admin_user");

            // Cleanup Spotfix SDK from DOM
            if (typeof window !== "undefined") {
                if (window.SpotFix?.logout) {
                    try {
                        window.SpotFix.logout();
                    } catch (e) {
                        console.warn("[SpotFix] Error logging out admin", e);
                    }
                }
                const script = document.getElementById("spotfix-sdk-script");
                if (script) script.remove();
                const widgetElements = document.querySelectorAll(
                    '[class*="spotfix"], [id*="spotfix"], [class*="viewtheme"], [id*="viewtheme"]'
                );
                widgetElements.forEach((el) => el.remove());
            }
        },

        clearAdminError: (state) => {
            state.error = null;
        }
    },
});

export const { adminLoginSuccess, adminLogout, clearAdminError } = adminAuthSlice.actions;
export default adminAuthSlice.reducer;
